import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { ChevronDown, ChevronRight, Mail } from 'lucide-react-native';
import { colors, helpArticles, searchHelp, SUPPORT_ADDRESS } from '@hogar/shared';
import { Button, Card, Field, Screen, Txt, useT } from '../components/ui';
import { BackHeader } from '../components/BackHeader';
import { buildLabel, openHelpEmail } from '../lib/help';

/**
 * Help (company standard, section 6): every Tier-1 answer from docs/SUPPORT.md, searchable by the
 * message the screen showed, in the owner's language, with the version so Mike can ask what it says.
 * Reachable signed out too (from the sign-in screen). `?a=<id>` opens one article.
 */
export default function Help() {
  const { t, lang } = useT();
  const { a } = useLocalSearchParams<{ a?: string }>();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(a && helpArticles.some((x) => x.id === a) ? a : null);
  const found = searchHelp(query, lang);

  return (
    <Screen>
      <BackHeader title={t('help')} fallback="/mas" />
      <Field value={query} onChange={setQuery} placeholder={t('helpSearch')} maxLength={300} />
      <View style={{ height: 12 }} />
      {found.length === 0 && (
        <Txt color={colors.textMuted} style={{ marginBottom: 12 }}>
          {t('helpNoMatch')}
        </Txt>
      )}
      {found.map((article) => {
        const shown = open === article.id || (query.trim() !== '' && found.length === 1);
        const Chevron = shown ? ChevronDown : ChevronRight;
        return (
          <Card key={article.id}>
            <Pressable onPress={() => setOpen(shown ? null : article.id)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14 }} accessibilityRole="button">
              <Txt weight="medium" size={16} style={{ flex: 1 }}>
                {article.title[lang]}
              </Txt>
              <Chevron size={20} color={colors.textMuted} />
            </Pressable>
            {shown && (
              <View style={{ gap: 10, paddingBottom: 14 }}>
                {article.body[lang].map((p, i) => (
                  <Txt key={i} size={15} style={{ lineHeight: 22 }}>
                    {p}
                  </Txt>
                ))}
              </View>
            )}
          </Card>
        );
      })}
      <Card style={{ marginTop: 14, paddingVertical: 14, gap: 10 }}>
        <Txt size={15}>{t('helpReply')}</Txt>
        <Button label={`${t('helpWrite')}: ${SUPPORT_ADDRESS}`} Icon={Mail} variant="outline" onPress={() => openHelpEmail(lang)} />
      </Card>
      <Txt size={13} color={colors.textMuted} style={{ textAlign: 'center', marginTop: 8 }} selectable>
        {t('appVersion', { v: buildLabel() })}
      </Txt>
    </Screen>
  );
}
